import { plainToClass } from "class-transformer";
import { Appendix } from "../../../../../../shared/entities/scripts/appendix";
import { Study } from "../../../../../../shared/entities/scripts/study";
import { FilterOption } from "../../../../../../shared/scripts/filtering/filtering.entities";
import { SetActiveTab } from "../../../../../../shared/scripts/tab-utilities";
import { SiteInfo } from "../../../../sites/shared/sites.entities";
import { ISiteInfo } from "../../../scripts/data-completeness.page";
import { OutcomesDatatableView } from "../outcomes-datatable-view/outcomes-datatable-view";
import { OutcomesDatatableEntry } from "../outcomes-datatable-view/outcomes-datatable.entities";
import { OutcomesSummaryView } from "../outcomes-summary-view/outcomes-summary-view";
import { OutcomesSummarySeries } from "../outcomes-summary-view/outcomes-summary.entities";
import { Outcome, OutcomeEvent } from "./outcomes.entities";
import { OutcomesLookup } from "./outcomes.lookup";


export class OutcomesController {

    siteInfo: ISiteInfo;

    allOutcomes: Outcome[] = [];
    allOutcomeEvents: OutcomeEvent[] = [];

    summaryView: OutcomesSummaryView;
    datatableView: OutcomesDatatableView;

    constructor(siteInfo: ISiteInfo, outcomesTimingJson: any) {

        this.siteInfo = siteInfo;

        let foundOutcomeIds: number[] = [];
        this.allOutcomeEvents = plainToClass(OutcomeEvent, <OutcomeEvent[]>outcomesTimingJson);
        this.allOutcomeEvents.forEach((o: OutcomeEvent) => {
            o.initialize()

            if (!foundOutcomeIds.includes(o.OutcomeId)) {
                this.allOutcomes.push(new Outcome(o.OutcomeId, OutcomesLookup.GetOutcome(o.OutcomeId), OutcomesLookup.GetOutcomeOrdering(o.OutcomeId)));
                foundOutcomeIds.push(o.OutcomeId);
            }

        });
        this.allOutcomes.sort((a: Outcome, b: Outcome) => a.OrderBy - b.OrderBy);

        this.summaryView = new OutcomesSummaryView();
        this.datatableView = new OutcomesDatatableView();

        //console.log(this.allOutcomes);
        //console.log(this.allOutcomeEvents);
    }

    FilterUpdated(studyIds: number[], appendixIds: number[], siteIds: number[], outcomeIds: number[]): void {

        let byOutcomeSummaryMap: Map<number, OutcomesSummarySeries> = new Map<number, OutcomesSummarySeries>();
        let bySiteSummaryMap: Map<number, OutcomesSummarySeries> = new Map<number, OutcomesSummarySeries>();

        let noGroupingDataMap: Map<string, OutcomesDatatableEntry> = new Map<string, OutcomesDatatableEntry>();
        let groupingByOutcomeDataMap: Map<string, OutcomesDatatableEntry> = new Map<string, OutcomesDatatableEntry>();
        let groupingBySiteDataMap: Map<string, OutcomesDatatableEntry> = new Map<string, OutcomesDatatableEntry>();

        //for loop instead of forEach for performance given there could be tens of thousands of visit events across all trials
        for (let i = 0; i < this.allOutcomeEvents.length; ++i) {
            let outcome: OutcomeEvent = this.allOutcomeEvents[i];

            if (!studyIds.includes(outcome.StudyId) || !appendixIds.includes(outcome.AppendixId) ||
                !outcomeIds.includes(outcome.OutcomeId) || !siteIds.includes(outcome.SiteNumber)) continue;

            //Summary View Data
            let outcomeSeries: OutcomesSummarySeries;
            if (byOutcomeSummaryMap.has(outcome.OutcomeId)) { outcomeSeries = byOutcomeSummaryMap.get(outcome.OutcomeId); }
            else { outcomeSeries = new OutcomesSummarySeries(OutcomesLookup.GetOutcome(outcome.OutcomeId), "outcome", outcome.OutcomeId); }
            outcomeSeries.addOutcomeEvent(outcome);
            byOutcomeSummaryMap.set(outcome.OutcomeId, outcomeSeries);

            let siteSeries: OutcomesSummarySeries;
            if (bySiteSummaryMap.has(outcome.SiteNumber)) { siteSeries = bySiteSummaryMap.get(outcome.SiteNumber); }
            else { siteSeries = new OutcomesSummarySeries(this.siteInfo.GetSiteName(outcome.SiteNumber), "site", outcome.SiteNumber); }
            siteSeries.addOutcomeEvent(outcome);
            bySiteSummaryMap.set(outcome.SiteNumber, siteSeries);

            //Datatable View Data
            let dataHash: string;

            let noGroupingData: OutcomesDatatableEntry;
            dataHash = OutcomesDatatableEntry.getHash("none", outcome);
            if (noGroupingDataMap.has(dataHash)) { noGroupingData = noGroupingDataMap.get(dataHash); }
            else { noGroupingData = new OutcomesDatatableEntry(Study.GetStudyName(outcome.StudyId), Appendix.GetAppendixName(outcome.AppendixId), this.siteInfo.GetSiteName(outcome.SiteNumber), SiteInfo.FormatSiteNumber(outcome.SiteNumber), OutcomesLookup.GetOutcome(outcome.OutcomeId)); }
            noGroupingData.addOutcomeEvent(outcome);
            noGroupingDataMap.set(dataHash, noGroupingData);

            let outcomeGroupingData: OutcomesDatatableEntry;
            dataHash = OutcomesDatatableEntry.getHash("by-outcome", outcome);
            if (groupingByOutcomeDataMap.has(dataHash)) { outcomeGroupingData = groupingByOutcomeDataMap.get(dataHash); }
            else { outcomeGroupingData = new OutcomesDatatableEntry(Study.GetStudyName(outcome.StudyId), Appendix.GetAppendixName(outcome.AppendixId), "All Sites", "All Sites", OutcomesLookup.GetOutcome(outcome.OutcomeId)); }
            outcomeGroupingData.addOutcomeEvent(outcome);
            groupingByOutcomeDataMap.set(dataHash, outcomeGroupingData);

            let siteGroupingData: OutcomesDatatableEntry;
            dataHash = OutcomesDatatableEntry.getHash("by-site", outcome);
            if (groupingBySiteDataMap.has(dataHash)) { siteGroupingData = groupingBySiteDataMap.get(dataHash); }
            else { siteGroupingData = new OutcomesDatatableEntry(Study.GetStudyName(outcome.StudyId), Appendix.GetAppendixName(outcome.AppendixId), this.siteInfo.GetSiteName(outcome.SiteNumber), SiteInfo.FormatSiteNumber(outcome.SiteNumber), "All Outcomes (n=" + Appendix.GetOutcomeCount(outcome.AppendixId) + ")"); }
            siteGroupingData.addOutcomeEvent(outcome);
            groupingBySiteDataMap.set(dataHash, siteGroupingData);
        }

        let seriesByVisit: OutcomesSummarySeries[] = [...byOutcomeSummaryMap.values()];
        seriesByVisit.sort((a: OutcomesSummarySeries, b: OutcomesSummarySeries) => a.sortValue - b.sortValue);

        let seriesBySite: OutcomesSummarySeries[] = [...bySiteSummaryMap.values()];
        seriesBySite.sort((a: OutcomesSummarySeries, b: OutcomesSummarySeries) => b.sortValue - a.sortValue);

        this.summaryView.UpdateData(seriesByVisit, seriesBySite);


        let tableEntriesNone: OutcomesDatatableEntry[] = [...noGroupingDataMap.values()];
        for (let i: number = 0; i < tableEntriesNone.length; ++i) { tableEntriesNone[i].initialize(); }
        //sort it somehow

        let tableEntriesByOutcome: OutcomesDatatableEntry[] = [...groupingByOutcomeDataMap.values()];
        for (let i: number = 0; i < tableEntriesByOutcome.length; ++i) { tableEntriesByOutcome[i].initialize(); }

        let tableEntriesBySite: OutcomesDatatableEntry[] = [...groupingBySiteDataMap.values()];
        for (let i: number = 0; i < tableEntriesBySite.length; ++i) { tableEntriesBySite[i].initialize(); }

        //console.log(noGroupingDataMap);

        this.datatableView.UpdateData(tableEntriesNone, tableEntriesByOutcome, tableEntriesBySite);
    }

    setView(newView: string): void {

        //console.log("outcomes.setView", newView);

        if (newView == "view-summary") { SetActiveTab("outcomes-tabs", "outcomes-summary-content"); this.summaryView.SetView("outcomes-summary"); }
        else if (newView == "view-datatable") { SetActiveTab("outcomes-tabs", "outcomes-datatable-content"); this.datatableView.SetView("outcomes-datatable-none"); }
        else {
            this.summaryView.SetView(newView);
            this.datatableView.SetView(newView);
        }
    }

   /* exportTable(): void {
        this.datatableView.exportTable();
    }*/
}